import type { ChipSpec, EnumGroup, Tone } from "./api/enums";

const chip = (label: string, tone: Tone): ChipSpec => ({ label, tone });

/** Plain-word label and tone for every status-like enum (docs/ui/ui-design.md section 5).
 * Keys are the group names in `enums.json`. A test fails if the backend adds a value that is not
 * mapped here, so a new state cannot ship without a label. */
// Warn is brass ("needs you"): a label that only classifies (an origin, a direction, a retry the
// system handles itself) is neutral, so brass keeps one meaning.
export const STATUS: Partial<Record<EnumGroup, Record<string, ChipSpec>>> = {
  stages: {
    requirement_gathering: chip("Requirements", "info"),
    vendor_outreach: chip("Vendor outreach", "info"),
    package_confirmation: chip("Package", "info"),
    booked: chip("Booked", "ok"),
    event_live: chip("Live", "ok"),
    archived: chip("Archived", "neutral"),
  },
  processing_states: {
    idle: chip("Idle", "neutral"),
    awaiting_agent: chip("Working", "info"),
    awaiting_admin: chip("Needs an admin", "warn"),
    awaiting_host: chip("Waiting on the host", "neutral"),
    takeover: chip("Taken over", "neutral"),
    failed: chip("Failed", "danger"),
  },
  proposal_states: {
    pending: chip("Pending", "warn"),
    approved: chip("Approved", "ok"),
    rejected: chip("Rejected", "danger"),
    changes_requested: chip("Changes requested", "warn"),
    cancelled: chip("Cancelled", "neutral"),
    expired: chip("Expired", "neutral"),
  },
  message_statuses: {
    stored: chip("Received", "neutral"),
    processed: chip("Handled", "neutral"),
    unmatched: chip("Unmatched", "warn"),
    draft: chip("Draft", "neutral"),
    approved: chip("Approved", "info"),
    queued: chip("Queued", "info"),
    sent: chip("Sent", "info"),
    delivered: chip("Delivered", "ok"),
    bounced: chip("Bounced", "danger"),
    complained: chip("Spam complaint", "danger"),
    failed: chip("Failed", "danger"),
    invalidated: chip("Withdrawn", "neutral"),
  },
  message_classifications: {
    normal: chip("Normal", "neutral"),
    auto_reply: chip("Auto-reply", "neutral"),
    bounce: chip("Bounce", "danger"),
    ooo: chip("Out of office", "neutral"),
    vendor_suspected: chip("May be a vendor", "warn"),
  },
  intake_kinds: {
    uncertain_spam: chip("Maybe spam", "warn"),
    vendor_suspected: chip("May be a vendor", "warn"),
    unmatched_reply: chip("Unmatched reply", "warn"),
    ambiguous_match: chip("Could be several events", "warn"),
  },
  intake_statuses: {
    open: chip("Open", "warn"),
    accepted_as_host: chip("Accepted as host", "neutral"),
    matched_to_event: chip("Matched to event", "neutral"),
    discarded_spam: chip("Discarded", "neutral"),
    routed_as_vendor: chip("Sent to vendor", "neutral"),
  },
  scan_statuses: {
    pending: chip("Scanning", "neutral"),
    clean: chip("Clean", "ok"),
    blocked: chip("Blocked", "danger"),
  },
  share_states: {
    auto: chip("Shared automatically", "neutral"),
    admin_shared: chip("Shared by an admin", "ok"),
    withheld: chip("Withheld", "neutral"),
  },
  extraction_statuses: {
    pending: chip("Waiting", "neutral"),
    running: chip("Reading", "info"),
    done: chip("Read", "ok"),
    no_text_layer: chip("No text found", "warn"),
    skipped: chip("Skipped", "neutral"),
    failed: chip("Could not read", "danger"),
  },
  vendor_statuses: {
    active: chip("Active", "ok"),
    archived: chip("Archived", "neutral"),
    blocked: chip("Blocked", "danger"),
  },
  candidate_statuses: {
    candidate: chip("Candidate", "neutral"),
    shortlisted: chip("Shortlisted", "info"),
    contacted: chip("Contacted", "info"),
    quoted: chip("Quoted", "ok"),
    selected: chip("Selected", "ok"),
    declined: chip("Declined", "neutral"),
    removed: chip("Removed", "neutral"),
  },
  rfq_statuses: {
    draft: chip("Draft", "neutral"),
    sent: chip("Sent", "info"),
    vendor_replied: chip("Vendor replied", "info"),
    quoted: chip("Quoted", "ok"),
    declined: chip("Declined", "neutral"),
    expired: chip("Expired", "danger"),
    superseded: chip("Replaced", "neutral"),
  },
  quote_statuses: {
    active: chip("Active", "ok"),
    selected: chip("Selected", "ok"),
    expired: chip("Expired", "danger"),
    superseded: chip("Replaced", "neutral"),
    withdrawn: chip("Withdrawn", "neutral"),
  },
  package_statuses: {
    draft: chip("Draft", "neutral"),
    published: chip("Published", "info"),
    accepted: chip("Accepted", "ok"),
    superseded: chip("Replaced", "neutral"),
    withdrawn: chip("Withdrawn", "neutral"),
    expired: chip("Expired", "danger"),
  },
  booking_statuses: {
    pending: chip("Not confirmed", "warn"),
    confirmed: chip("Confirmed", "ok"),
    needs_reconfirmation: chip("Needs re-confirming", "warn"),
    declined_after_payment: chip("Declined after payment", "danger"),
    cancelled: chip("Cancelled", "neutral"),
    completed: chip("Completed", "ok"),
    no_show: chip("No-show", "danger"),
  },
  payment_states: {
    checkout_created: chip("Awaiting payment", "info"),
    partially_paid: chip("Part paid", "warn"),
    paid: chip("Paid", "ok"),
    held: chip("Held", "ok"),
    partially_released: chip("Partly released", "info"),
    released: chip("Released", "ok"),
    refunded: chip("Refunded", "neutral"),
    partially_refunded: chip("Partly refunded", "neutral"),
    disputed: chip("Disputed", "danger"),
    failed: chip("Failed", "danger"),
    expired: chip("Expired", "neutral"),
  },
  intent_statuses: {
    checkout_created: chip("Awaiting payment", "info"),
    paid: chip("Paid", "ok"),
    checkout_expired: chip("Expired", "neutral"),
    failed: chip("Failed", "danger"),
    refunded: chip("Refunded", "neutral"),
  },
  invoice_states: {
    draft: chip("Draft", "neutral"),
    open: chip("Awaiting payment", "info"),
    paid: chip("Paid", "ok"),
    paid_outside_stripe: chip("Paid outside Stripe", "ok"),
    void: chip("Void", "neutral"),
  },
  payout_states: {
    scheduled: chip("Scheduled", "neutral"),
    awaiting_approval: chip("Needs approval", "warn"),
    held: chip("Held", "warn"),
    approved: chip("Approved", "info"),
    transferred: chip("Paid out", "ok"),
    failed: chip("Failed", "danger"),
    reversed: chip("Reversed", "danger"),
    cancelled: chip("Cancelled", "neutral"),
  },
  refund_states: {
    requested: chip("Requested", "warn"),
    rejected: chip("Rejected", "neutral"),
    // Left approved only when no Stripe charge covers it (C): it waits to be paid back by hand.
    approved: chip("Refund outside Stripe", "warn"),
    submitted: chip("Submitted", "info"),
    succeeded: chip("Refunded", "ok"),
    failed: chip("Failed", "danger"),
  },
  recovery_states: {
    none: chip("None", "neutral"),
    pending: chip("Recovering", "neutral"),
    recovered: chip("Recovered", "ok"),
    written_off: chip("Written off", "neutral"),
  },
  dispute_statuses: {
    open: chip("Open", "danger"),
    resolved_release: chip("Resolved: released", "ok"),
    resolved_partial_refund: chip("Resolved: part refund", "ok"),
    resolved_full_refund: chip("Resolved: full refund", "ok"),
    lost: chip("Lost", "danger"),
    won: chip("Won", "ok"),
  },
  dispute_origins: {
    host_report: chip("Reported by host", "warn"),
    chargeback: chip("Chargeback", "danger"),
  },
  rating_statuses: {
    pending_review: chip("Needs review", "warn"),
    approved: chip("Approved", "ok"),
    rejected: chip("Rejected", "neutral"),
  },
  job_states: {
    queued: chip("Queued", "neutral"),
    running: chip("Running", "info"),
    succeeded: chip("Done", "ok"),
    failed: chip("Failed, will retry", "neutral"),
    dead: chip("Gave up", "danger"),
    cancelled: chip("Cancelled", "neutral"),
  },
  outbox_states: {
    pending: chip("Waiting to send", "info"),
    claimed: chip("Sending", "info"),
    sent: chip("Sent", "ok"),
    failed: chip("Failed, will retry", "neutral"),
    dead: chip("Gave up", "danger"),
    cancelled: chip("Cancelled", "neutral"),
  },
  event_flags: {
    host_quiet: chip("Host quiet", "warn"),
    stale_brief: chip("Brief is stale", "warn"),
    proposal_overdue: chip("Approval overdue", "danger"),
    event_live_due: chip("Ready to go live", "info"),
    closure_required: chip("Closure required", "warn"),
    archive_overdue: chip("Archive overdue", "danger"),
    instant_replies: chip("Instant replies", "info"),
    quiet_hours_off: chip("Quiet hours off", "info"),
  },
  notification_priorities: {
    normal: chip("Normal", "neutral"),
    high: chip("High", "danger"),
  },
  actor_kinds: {
    admin: chip("Staff", "info"),
    agent: chip("Agent", "neutral"),
    system: chip("System", "neutral"),
    host: chip("Host", "ok"),
    stripe: chip("Stripe", "neutral"),
    mailgun: chip("Mailgun", "neutral"),
  },
  // Only the first and last count as confirmed; inferred and attachment values wait for the host.
  brief_origins: {
    host_stated: chip("Host stated", "ok"),
    admin: chip("Admin", "neutral"),
    inferred: chip("Inferred", "neutral"),
    attachment: chip("From attachment", "neutral"),
  },
  // Who a person is on the event: classification, so neutral. The honoree's "never contacted"
  // is said in words beside the chip, not by tone.
  contact_roles: {
    host: chip("Host", "neutral"),
    payer: chip("Payer", "neutral"),
    honoree: chip("Honoree", "neutral"),
    participant: chip("Participant", "neutral"),
  },
  // Sourced and confirmed are read from quotes and bookings; an admin sets the rest.
  delivery_statuses: {
    planned: chip("Planned", "neutral"),
    sourced: chip("Quoted", "info"),
    confirmed: chip("Confirmed", "ok"),
    on_the_way: chip("On the way", "info"),
    delivered: chip("Delivered", "ok"),
    cancelled: chip("Cancelled", "neutral"),
  },
  note_kinds: {
    occasion: chip("Occasion", "neutral"),
    attendees: chip("Attendees", "neutral"),
    priority: chip("Priority", "neutral"),
    concern: chip("Concern", "neutral"),
    other: chip("Note", "neutral"),
  },
  question_statuses: {
    open: chip("Not asked yet", "warn"),
    asked: chip("Asked", "info"),
    answered: chip("Answered", "ok"),
    dropped: chip("Dropped", "neutral"),
  },
  impact_statuses: {
    open: chip("Open", "warn"),
    resolved: chip("Resolved", "ok"),
    dismissed: chip("Dismissed", "neutral"),
  },
  stage_directions: {
    forward: chip("Moved forward", "info"),
    send_back: chip("Sent back", "neutral"),
  },
  agent_run_statuses: {
    running: chip("Running", "info"),
    completed: chip("Completed", "ok"),
    validation_failed: chip("Bad output", "danger"),
    failed: chip("Failed", "danger"),
    stale: chip("Stale", "neutral"),
    invalidated: chip("Invalidated", "neutral"),
    timed_out: chip("Timed out", "danger"),
  },
  agent_applied_statuses: {
    applied: chip("Applied", "ok"),
    partially_applied: chip("Partly applied", "neutral"),
    rejected: chip("Rejected", "neutral"),
    stale: chip("Stale", "neutral"),
    superseded: chip("Superseded", "neutral"),
  },
};

/** The chip for a value, or a neutral humanised fallback so an unknown value never breaks a screen. */
export function chipFor(group: EnumGroup, value: string): ChipSpec {
  return STATUS[group]?.[value] ?? chip(humanize(value), "neutral");
}

export function humanize(value: string): string {
  const spaced = value.replace(/[_.:]+/g, " ").trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
